import { computed, toValue } from 'vue';
import { useQuery, useQueryClient, useMutation } from '@tanstack/vue-query';
import { QueryGcTime } from '@/constants/http';
import { AUTH0_LATENCY_DELAY } from '@/constants/auth0.js';
import api from './api';
import { useSupplierUsersStore } from '@/stores/supplierUsers.js';

export const useSuppliers = (params, enabled = true) => {
  return useQuery({
    queryKey: ['suppliers', params],
    queryFn: () => api.getAll(params),
    enabled,
  });
};

export const useSupplierById = (id, retry = 3) => {
  return useQuery({
    queryKey: ['supplier', computed(() => id)],
    queryFn: async () => await api.getById(toValue(id)),
    enabled: computed(() => !!toValue(id)),
    retry,
  });
};

export const useTransferSenders = (supplierId, options = { gcTime: QueryGcTime }) => {
  const enabled = computed(() => !!supplierId.value);
  return useQuery({
    queryKey: ['transfer-senders', supplierId],
    queryFn: () => api.getTransferSenders(toValue(supplierId)),
    gcTime: options.gcTime,
    enabled,
  });
};

export const useRoomTypes = (supplierId, params = {}) => {
  return useQuery({
    queryKey: ['room-types', supplierId, params],
    queryFn: () => api.getRoomTypes(toValue(supplierId), params),
    enabled: computed(() => !!supplierId.value),
  });
};

export const useRateCodes = (supplierId, params = {}, select = (data) => data) => {
  return useQuery({
    queryKey: ['rate-codes', supplierId, params],
    queryFn: () => api.getRateCodes(toValue(supplierId), toValue(params)),
    enabled: computed(() => !!supplierId.value),
    select,
  });
};

export const useSearchRateCodes = (supplierId, selectFromResult = (item) => item) => {
  const queryClient = useQueryClient();
  return async ({ search = '' }) => {
    const searchParams = {
      search_text: search,
      sort_by: search.length ? 'relevance' : 'created_at',
      per_page: 25,
    };

    try {
      const data = await queryClient.fetchQuery({
        queryKey: ['rate-codes', toValue(supplierId), searchParams],
        queryFn: () => api.getRateCodes(toValue(supplierId), searchParams),
      });
      return data.data.map(selectFromResult);
    } catch {
      return [];
    }
  };
};

export const useRoles = (enabled) => {
  return useQuery({
    queryKey: ['roles'],
    queryFn: () => api.getRoles(),
    enabled: computed(() => !!toValue(enabled)),
    select: (data) =>
      // unfortunately, the API returns the roles in a random order,
      // so we need to sort them by name with 'Admin' being the first role
      data.data?.sort((a, b) => {
        if (a.name === 'Admin') return -1;
        if (b.name === 'Admin') return 1;
        return a.name.localeCompare(b.name);
      }) || [],
  });
};

export const useUsersBySupplierId = (id) => {
  const { filters } = useSupplierUsersStore();
  return useQuery({
    queryKey: ['supplier-users', id, filters],
    queryFn: async () => await api.getUsersBySupplierId({ id: toValue(id), params: filters }),
    enabled: computed(() => !!toValue(id)),
  });
};

export const useCreateUser = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => {
      return api.createSupplierUser({
        id: toValue(id),
        data,
      });
    },
    onSuccess: () => {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['supplier-users', toValue(id)] });
      }, AUTH0_LATENCY_DELAY);
    },
  });
};
